/** Minimal data-fetching hooks — no cache library, no suspense, no magic. */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api.js";
/**
 * Runs `fn` on mount and whenever `deps` change. `fn` must be stable enough —
 * pass an inline arrow and the deps array it closes over.
 */
export function useAsync(fn, deps) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initial, setInitial] = useState(true);
    const [nonce, setNonce] = useState(0);
    const seq = useRef(0);
    useEffect(() => {
        const ctrl = new AbortController();
        const mySeq = ++seq.current;
        setLoading(true);
        setError(null);
        fn(ctrl.signal)
            .then((v) => {
            if (mySeq !== seq.current)
                return;
            setData(v);
        })
            .catch((e) => {
            if (mySeq !== seq.current)
                return;
            if (e?.name === "AbortError")
                return;
            setError(e instanceof ApiError
                ? e
                : new ApiError(0, { title: "Unexpected error", detail: String(e?.message ?? e) }));
        })
            .finally(() => {
            if (mySeq !== seq.current)
                return;
            setLoading(false);
            setInitial(false);
        });
        return () => ctrl.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, nonce]);
    const reload = useCallback(() => setNonce((n) => n + 1), []);
    return { data, error, loading, initial, reload, setData };
}
export function useMutation(fn) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const alive = useRef(true);
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);
    const run = useCallback(async (...args) => {
        setLoading(true);
        setError(null);
        try {
            const r = await fn(...args);
            if (alive.current)
                setResult(r);
            return r;
        }
        catch (e) {
            const err = e instanceof ApiError
                ? e
                : new ApiError(0, { title: "Unexpected error", detail: String(e?.message ?? e) });
            if (alive.current)
                setError(err);
            throw err;
        }
        finally {
            if (alive.current)
                setLoading(false);
        }
    }, [fn]);
    const reset = useCallback(() => {
        setError(null);
        setResult(null);
    }, []);
    return { run, loading, error, result, reset };
}
/** Debounce a rapidly-changing value (search boxes). */
export function useDebounced(value, ms = 300) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setV(value), ms);
        return () => clearTimeout(id);
    }, [value, ms]);
    return v;
}
/** Persisted key/value state (filters that should survive a reload). */
export function usePersisted(key, initial) {
    const [v, setV] = useState(() => {
        try {
            const raw = localStorage.getItem(key);
            return raw ? JSON.parse(raw) : initial;
        }
        catch {
            return initial;
        }
    });
    const set = useCallback((next) => {
        setV(next);
        try {
            localStorage.setItem(key, JSON.stringify(next));
        }
        catch {
            /* quota — ignore */
        }
    }, [key]);
    return [v, set];
}
